import { useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

interface GhostClickGuard {
  onPointerDownCapture(event: ReactPointerEvent<HTMLElement>): void;
  onClickCapture(event: ReactMouseEvent<HTMLElement>): void;
}

/**
 * Swallows the ghost click of the gesture that opened a floating panel: the tap (or click) on a
 * block or the lane opens the panel on pointerup, and the browser's synthesized `click` that
 * follows lands on whatever panel element is now under the pointer — on the taller sheet that can
 * be a project row, which would choose a project the user never aimed at. A pointer-generated
 * click that reaches the panel without a preceding pointerdown inside it is such a ghost and is
 * cancelled; keyboard activations (`detail` 0) always pass.
 */
export function useGhostClickGuard(): GhostClickGuard {
  const sawPointerDown = useRef(false);
  return {
    onPointerDownCapture: () => {
      sawPointerDown.current = true;
    },
    onClickCapture: (event) => {
      if (sawPointerDown.current || event.detail === 0) return;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}
